import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { PostcodeDistrict, PlanningRecord } from '@leadgen/models/client'
import useCallbackDebounced from '../../hooks/shared/useCallbackDebounced'
import planningRecordForm, { FormData } from '../../forms/planningRecordForm'
import { useDispatch } from '../../redux/hooks'
import { useAutocomplete } from '../../hooks/address/useAutocomplete'
import planningRecordsActions from '../../redux/modules/planningRecords/actions'
import { AutocompleteSuggestionDetails } from '../../services/addressService'

const defaultPlanningCategories = []
const defaultPlanningClassifications = []

const getDefaultValues = (planningRecord: PlanningRecord) => {
  const values: FormData = {
    id: planningRecord.id,
    applicantTitle: planningRecord.applicantTitle,
    applicantCompanyName: planningRecord.applicantCompanyName,
    applicantFirstName: planningRecord.applicantFirstName,
    applicantLastName: planningRecord.applicantLastName,
    applicantAddress1: planningRecord.applicantAddress1,
    applicantAddress2: planningRecord.applicantAddress2,
    applicantPostcode: planningRecord.applicantPostcode,
    applicantTown: planningRecord.applicantTown,
    agentTitle: planningRecord.agentTitle,
    agentCompanyName: planningRecord.agentCompanyName,
    agentFirstName: planningRecord.agentFirstName,
    agentLastName: planningRecord.agentLastName,
    agentAddress1: planningRecord.agentAddress1,
    agentAddress2: planningRecord.agentAddress2,
    agentPostcode: planningRecord.agentPostcode,
    agentTown: planningRecord.agentTown,
    categories: planningRecord.categories,
    classifications: planningRecord.classifications,
    initialAddress: planningRecord.address,
    initialPostcode: planningRecord.postcode,
    initialPostcodeDistrict: planningRecord.postcodeDistrict,
    address1: null,
    address2: null,
    town: null,
    postcode: planningRecord.postcode,
    postcodeDistrict: planningRecord.postcodeDistrict,
    recipientType:
      planningRecord.recipientType ||
      (!planningRecord.applicantPostcode &&
      planningRecord.applicantAddressPredictionStrength !== 'weak' &&
      planningRecord.agentPostcode
        ? 'agent'
        : 'applicant'),
    applicationType: planningRecord.applicationType,
  }
  return values
}

export const usePlanningRecordForm = ({ planningRecord }: { planningRecord?: PlanningRecord }) => {
  const [addressAutoCompleteDirty, setAddressAutoCompleteDirty] = useState(false)
  const [applicantAutoCompleteDirty, setApplicantAutoCompleteDirty] = useState(false)
  const [agentAutoCompleteDirty, setAgentAutoCompleteDirty] = useState(false)
  const dispatch = useDispatch()
  const {
    control,
    handleSubmit: handleSubmitApprove,
    reset,
    setError,
    setValue,
    trigger,
    getValues,
    formState,
    getFieldState,
    ...rest
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: planningRecordForm.resolver,
    defaultValues: planningRecord ? getDefaultValues(planningRecord) : undefined,
  })
  const { handleSubmit: handleSubmitDiscard } = useForm<{ id: string }>({
    values: planningRecord
      ? {
          id: planningRecord.id,
        }
      : undefined,
  })
  useEffect(() => {
    if (!planningRecord) return
    reset(getDefaultValues(planningRecord), {
      keepDirtyValues: true,
      keepDirty: true,
    })
    queueMicrotask(() => {
      trigger()
    })
  }, [reset, planningRecord, trigger])
  const postcode = useWatch({ control, name: 'postcode' })
  const recipientType = useWatch({ control, name: 'recipientType' })
  const applicantPostcode = useWatch({ control, name: 'applicantPostcode' })
  const applicantAddress1 = useWatch({ control, name: 'applicantAddress1' })
  const applicantAddress2 = useWatch({ control, name: 'applicantAddress2' })
  const applicantTown = useWatch({ control, name: 'applicantTown' })
  const agentPostcode = useWatch({ control, name: 'agentPostcode' })
  const agentAddress1 = useWatch({ control, name: 'agentAddress1' })
  const agentAddress2 = useWatch({ control, name: 'agentAddress2' })
  const agentTown = useWatch({ control, name: 'agentTown' })
  useEffect(() => {
    if (postcode) {
      const nextPostcodeDistrict = (postcode as string)
        .trim()
        .slice(0, -3)
        .trim()
        .toUpperCase() as PostcodeDistrict
      const currentValues = getValues()
      if (currentValues.postcodeDistrict !== nextPostcodeDistrict) {
        setValue('postcodeDistrict', nextPostcodeDistrict, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      }
    }
  }, [postcode, getValues, setValue])
  const applicationType = useWatch({ control, name: 'applicationType' })
  const applicationTypeDirtyRef = useRef(false)
  useEffect(() => {
    const applicationTypeFieldState = getFieldState('applicationType')
    if (applicationTypeFieldState.isDirty) {
      applicationTypeDirtyRef.current = true
    }
    if (applicationTypeDirtyRef.current) {
      if (!applicationType) {
        setValue('classifications', defaultPlanningClassifications, { shouldValidate: true })
        setValue('categories', defaultPlanningCategories, { shouldValidate: true })
        return
      }
    }
  }, [applicationType, getFieldState, setValue, getValues])

  const watched = useWatch({
    control,
    name: [
      'applicantPostcode',
      'applicantAddress1',
      'applicantAddress2',
      'applicantTown',
      'applicantCompanyName',
      'applicantTitle',
      'applicantFirstName',
      'applicantLastName',
      'agentPostcode',
      'agentAddress1',
      'agentAddress2',
      'agentTown',
      'agentCompanyName',
      'agentTitle',
      'agentFirstName',
      'agentLastName',
    ],
  })

  useEffect(() => {
    trigger() // validate entire form, clears stale cross-field errors
  }, [watched, trigger])

  const showClassifications = !!applicationType
  const showCategories = !!applicationType
  const showPostcodeField =
    !planningRecord?.address || !planningRecord?.postcode || !planningRecord?.postcodeDistrict
  const showAddressFields = planningRecord && showPostcodeField && addressAutoCompleteDirty
  const showApplicantFields = recipientType === 'applicant'
  const showApplicantAddressFields =
    planningRecord &&
    showApplicantFields &&
    (applicantAddress1 ||
      planningRecord.applicantAddress1 ||
      applicantAddress2 ||
      planningRecord.applicantAddress2 ||
      applicantTown ||
      planningRecord.applicantTown ||
      applicantAutoCompleteDirty)
  const showAgentFields = recipientType === 'agent'
  const showAgentAddressFields =
    planningRecord &&
    showAgentFields &&
    (agentAddress1 ||
      planningRecord.agentAddress1 ||
      agentAddress2 ||
      planningRecord.agentAddress2 ||
      agentTown ||
      planningRecord.agentTown ||
      agentAutoCompleteDirty)
  const {
    loading: loadingAutocomleteAddress,
    loadingSelected: loadingAutocomleteAddressSelection,
    suggestions: suggestionsAddress,
    selectSuggestion: selectSuggestionAddress,
  } = useAutocomplete({
    onDetailsSelected: (details: AutocompleteSuggestionDetails) => {
      setAddressAutoCompleteDirty(true)
      setValue('address1', details.addressLine1, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('address2', details.addressLine2, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('postcode', details.postcode, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      const nextPostcodeDistrict = (details.postcode as string)
        .trim()
        .slice(0, -3)
        .trim()
        .toUpperCase() as PostcodeDistrict
      setValue('postcodeDistrict', nextPostcodeDistrict, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('town', details.town, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    },
    address:
      formState.dirtyFields['postcode'] && postcode
        ? postcode
        : planningRecord?.postcode || planningRecord?.address || '',
  })
  const {
    loading: loadingAutocomleteApplicant,
    loadingSelected: loadingAutocomleteApplicantSelection,
    suggestions: suggestionsApplicant,
    selectSuggestion: selectSuggestionApplicant,
  } = useAutocomplete({
    onDetailsSelected: (details: AutocompleteSuggestionDetails) => {
      setApplicantAutoCompleteDirty(true)
      setValue('applicantAddress1', details.addressLine1, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('applicantAddress2', details.addressLine2, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('applicantPostcode', details.postcode, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('applicantTown', details.town, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    },
    address:
      formState.dirtyFields['applicantPostcode'] && applicantPostcode
        ? applicantPostcode
        : planningRecord
          ? planningRecord.applicantAddress1 === null &&
            planningRecord.applicantAddress2 === null &&
            planningRecord.applicantTown === null &&
            planningRecord.applicantPostcode === null
            ? planningRecord.address || ''
            : `${planningRecord.applicantAddress1 ? `${planningRecord.applicantAddress1} ` : ''}${planningRecord.applicantAddress2 ? `${planningRecord.applicantAddress2} ` : ''}${planningRecord.applicantTown ? `${planningRecord.applicantTown} ` : ''}${planningRecord.applicantPostcode}`
          : '',
  })
  const {
    loading: loadingAutocomleteAgent,
    loadingSelected: loadingAutocomleteAgentSelection,
    suggestions: suggestionsAgent,
    selectSuggestion: selectSuggestionAgent,
  } = useAutocomplete({
    onDetailsSelected: (details: AutocompleteSuggestionDetails) => {
      setAgentAutoCompleteDirty(true)
      setValue('agentAddress1', details.addressLine1, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('agentAddress2', details.addressLine2, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('agentPostcode', details.postcode, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setValue('agentTown', details.town, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    },
    address:
      formState.dirtyFields['agentPostcode'] && agentPostcode
        ? agentPostcode
        : planningRecord
          ? planningRecord.agentAddress1 === null &&
            planningRecord.agentAddress2 === null &&
            planningRecord.agentTown === null &&
            planningRecord.agentPostcode === null
            ? planningRecord.address || ''
            : `${planningRecord.agentAddress1 ? `${planningRecord.agentAddress1} ` : ''}${planningRecord.agentAddress2 ? `${planningRecord.agentAddress2} ` : ''}${planningRecord.agentTown ? `${planningRecord.agentTown} ` : ''}${planningRecord.agentPostcode}`
          : '',
  })
  const onSubmitApprove: SubmitHandler<FormData> = useCallbackDebounced(
    async (values: FormData) => {
      if (!planningRecord) {
        setError('root', { message: 'Failed to submit.' })
        return
      }
      await planningRecordForm
        .onSubmitApprove(values)
        .then(() => {
          dispatch(planningRecordsActions.approvePlanningRecord(planningRecord))
        })
        .catch((error: Error) => {
          setError('root', { message: error.message })
        })
    },
    [dispatch, planningRecord, setError],
  )
  const onSubmitDiscard: SubmitHandler<{ id: string }> = useCallbackDebounced(
    async ({ id }: { id: string }) => {
      if (!planningRecord) {
        setError('root', { message: 'Failed to submit.' })
        return
      }
      await planningRecordForm
        .onSubmitDiscard({
          planningRecordId: id,
        })
        .then(() => {
          dispatch(planningRecordsActions.discardPlanningRecord(planningRecord))
        })
        .catch((error: Error) => {
          setError('root', { message: error.message })
        })
    },
    [dispatch, planningRecord, setError],
  )
  return {
    ...rest,
    getValues,
    control,
    formState,
    reset,
    handleSubmitApprove,
    handleSubmitDiscard,
    setError,
    setValue,
    trigger,
    onSubmitApprove,
    onSubmitDiscard,
    showApplicantFields,
    showApplicantAddressFields,
    showAgentFields,
    showAgentAddressFields,
    showPostcodeField,
    showAddressFields,
    recipientType,
    selectSuggestionApplicant,
    suggestionsApplicant,
    loadingAutocomleteApplicant,
    loadingAutocomleteApplicantSelection,
    selectSuggestionAgent,
    suggestionsAgent,
    loadingAutocomleteAgent,
    loadingAutocomleteAgentSelection,
    selectSuggestionAddress,
    suggestionsAddress,
    loadingAutocomleteAddress,
    loadingAutocomleteAddressSelection,
    showClassifications,
    showCategories,
  }
}
